import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  DropdownButton,
  Dropdown,
  ButtonGroup,
  Tooltip,
  Overlay,
} from 'react-bootstrap';
import { RiArrowDownSLine } from 'react-icons/ri';
import { TbClockHour4, TbX, TbCalendarEvent, TbSend } from 'react-icons/tb';
import Styles from './ButtonGroupComponent.module.scss';
import ScheduleForm from 'Containers/Editor/Components/ScheduleForm/ScheduleForm';
import { useSelector } from 'react-redux';
import { cancelScheduledPublish, publishNow } from 'services/documentService';
import { error as errorToast, success as successToast } from 'helpers/toaster';

const formatScheduledDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

function ButtonGroupComponent({
  document,
  setPublishModalView,
  handlePublishDoc,
  setDocumentHeaderTab,
  documentHeaderTab,
  onPublish,
  checkPublishDoc,
  onOpenPublishSettings,
  refetchContent,
}) {
  const [openModal, setOpenModal] = useState(false);
  const modalRef = useRef(null);

  // Local state for immediate UI feedback
  const [localStatus, setLocalStatus] = useState(document?.status);
  const [isLoading, setIsLoading] = useState(false);

  // Sync local state when document prop changes
  useEffect(() => {
    setLocalStatus(document?.status);
  }, [document?.status]);

  const { user } = useSelector((state) => state.auth);
  const { subscriptions } = user || {};
  const { current_subscription: currentSubscription } = subscriptions ?? {};

  const [showTooltip, setShowTooltip] = useState(false);
  const publishButtonRef = useRef(null);

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setOpenModal(false);
    }
  };

  useEffect(() => {
    if (openModal) {
      window.document.addEventListener('mousedown', handleClickOutside);
    } else {
      window.document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      window.document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openModal]);

  const handlePublishClick = () => {
    if (onOpenPublishSettings) {
      onOpenPublishSettings();
    } else {
      if (documentHeaderTab !== 'Publish') {
        setDocumentHeaderTab('Publish');
      } else {
        let isPublish = checkPublishDoc();
        if (isPublish === true) {
          onPublish();
        }
      }
    }
  };

  const isScheduled = localStatus === 'scheduled';
  const scheduledAt = document?.scheduled_at;

  const handleCancelSchedule = async () => {
    // Immediately update UI
    setLocalStatus('approved');
    setIsLoading(true);

    try {
      await cancelScheduledPublish(document.id);
      successToast('Schedule cancelled successfully');
      // Refresh data in background
      if (refetchContent) {
        refetchContent(false);
      }
    } catch (error) {
      // Revert on failure
      setLocalStatus('scheduled');
      errorToast('Failed to cancel schedule');
      console.error('Failed to cancel scheduled publish:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublishNow = async () => {
    // Immediately update UI
    setLocalStatus('published');
    setIsLoading(true);

    try {
      await publishNow(document.id);
      successToast('Published successfully');
      // Refresh data in background
      if (refetchContent) {
        refetchContent(false);
      }
    } catch (error) {
      // Revert on failure
      setLocalStatus('scheduled');
      errorToast('Failed to publish');
      console.error('Failed to publish immediately:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReschedule = () => {
    if (onOpenPublishSettings) {
      onOpenPublishSettings();
    }
  };

  // Render scheduled dropdown if document is scheduled
  if (isScheduled) {
    return (
      <div className={Styles.modal_container}>
        <Dropdown as={ButtonGroup}>
          <Dropdown.Toggle
            className={`secondary_btn ${Styles.scheduledButton}`}
            id="scheduled-dropdown"
            disabled={isLoading}
          >
            <TbClockHour4 style={{ marginRight: '6px' }} />
            {isLoading ? 'Processing...' : 'Scheduled'}
            <RiArrowDownSLine style={{ marginLeft: '4px' }} />
          </Dropdown.Toggle>

          <Dropdown.Menu className={Styles.scheduledMenu}>
            {scheduledAt && (
              <div className={Styles.scheduledInfo}>
                <TbCalendarEvent size={14} />
                <span>{formatScheduledDate(scheduledAt)}</span>
              </div>
            )}
            <Dropdown.Divider />
            <Dropdown.Item onClick={handlePublishNow} className={Styles.scheduledOption} disabled={isLoading}>
              <TbSend size={16} />
              <span>Publish Now</span>
            </Dropdown.Item>
            <Dropdown.Item onClick={handleReschedule} className={Styles.scheduledOption} disabled={isLoading}>
              <TbCalendarEvent size={16} />
              <span>Reschedule</span>
            </Dropdown.Item>
            <Dropdown.Item onClick={handleCancelSchedule} className={Styles.scheduledOption} style={{ color: '#dc3545' }} disabled={isLoading}>
              <TbX size={16} />
              <span>Cancel Schedule</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  }

  return (
    <>
      <div className={Styles.modal_container}>
        <ButtonGroup className="d-flex gap-1">
          <Button
            ref={publishButtonRef}
            className={`secondary_btn ${Styles.button}`}
            onClick={handlePublishClick}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : 'Publish'}
          </Button>
          <Overlay
            target={publishButtonRef.current}
            show={showTooltip}
            placement="top"
          >
            {(props) => (
              <Tooltip id="publish-tooltip" {...props}>
                Coming Soon
              </Tooltip>
            )}
          </Overlay>
        </ButtonGroup>
        {openModal && (
          <div ref={modalRef} className={Styles.modal_class}>
            <ScheduleForm />
          </div>
        )}
      </div>
    </>
  );
}

export default ButtonGroupComponent;
